/* eslint-disable no-plusplus */
import { useEffect, useRef } from 'react';

interface LauraGenerativeArtProps {
  params: {
    scale: number;
    count: number;
    twist: number;
    twirl: number;
    noise: number;
    frequency: number;
    velocity: number;
    ampX: number;
    ampY: number;
    lump: number;
    aspectRatio: number;
  };
}

export function LauraGenerativeArt({ params }: LauraGenerativeArtProps) {
  const canvasRef = useRef(null);
  const animationRef = useRef<number | null>(null);
  const mouseRef = useRef({ x: 0.5, y: 0.5 });
  const isAnimating = true;

  // Handle mouse movement
  useEffect(() => {
    const handleMouseMove = (e: any) => {
      const canvas: any = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      mouseRef.current = {
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
      };
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Main animation loop
  useEffect(() => {
    const canvas: any = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    // Set canvas size
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
    };

    resize();
    window.addEventListener('resize', resize);

    let time = 0;

    const draw = () => {
      if (!isAnimating) return;

      const width = canvas.width / dpr;
      const height = canvas.height / dpr;

      // Clear canvas with transparency
      ctx.clearRect(0, 0, width, height);

      const centerX = width / 2;
      const centerY = height / 2;

      // Draw generative pattern
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 1;

      for (let i = 0; i < params.count; i++) {
        const t = (i / params.count) * Math.PI * 2;
        const progress = i / params.count;

        // Base circular position
        let radius = (width / 4) * params.scale;

        // Apply twist
        const twistAmount = params.twist * progress;
        const angle = t + time * params.velocity + twistAmount;

        // Apply waves
        const waveX = Math.sin(t * params.frequency + time) * params.ampX * 30;
        const waveY = Math.cos(t * params.frequency + time) * params.ampY * 30;

        // Apply mouse influence
        const mouseInfluence = 100;
        const mouseDX = (mouseRef.current.x - 0.5) * mouseInfluence;
        const mouseDY = (mouseRef.current.y - 0.5) * mouseInfluence;

        // Apply twirl
        const twirlAngle =
          params.twirl * Math.sin(progress * Math.PI * 2 + time);
        const twirlX = Math.cos(twirlAngle) * 20;
        const twirlY = Math.sin(twirlAngle) * 20;

        // Apply noise
        const noiseX = (Math.random() - 0.5) * params.noise * 20;
        const noiseY = (Math.random() - 0.5) * params.noise * 20;

        // Apply lump (radial distortion)
        const lumpFactor = 1 + Math.sin(t * 3 + time) * params.lump;
        radius *= lumpFactor;

        // Calculate final position
        const x =
          centerX +
          Math.cos(angle) * radius * params.aspectRatio +
          waveX +
          mouseDX +
          twirlX +
          noiseX;
        const y =
          centerY +
          Math.sin(angle) * radius +
          waveY +
          mouseDY +
          twirlY +
          noiseY;

        // Draw ellipse
        ctx.beginPath();
        const ellipseWidth = 40 + Math.sin(t * 2 + time) * 20;
        const ellipseHeight = 20 + Math.cos(t * 3 + time) * 10;
        const rotation = angle + params.twist * progress;

        ctx.ellipse(
          x,
          y,
          ellipseWidth,
          ellipseHeight,
          rotation,
          0,
          Math.PI * 2,
        );
        ctx.stroke();

        // Draw connecting lines occasionally
        if (i > 0 && i % 5 === 0) {
          const prevT = ((i - 5) / params.count) * Math.PI * 2;
          const prevAngle =
            prevT +
            time * params.velocity +
            params.twist * ((i - 5) / params.count);
          const prevRadius =
            (width / 4) *
            params.scale *
            (1 + Math.sin(prevT * 3 + time) * params.lump);

          const prevX =
            centerX + Math.cos(prevAngle) * prevRadius * params.aspectRatio;
          const prevY = centerY + Math.sin(prevAngle) * prevRadius;

          ctx.beginPath();
          ctx.moveTo(prevX, prevY);
          ctx.lineTo(x, y);
          ctx.strokeStyle = `rgba(0, 0, 0, ${0.1 + params.lump * 0.2})`;
          ctx.lineWidth = 0.5;
          ctx.stroke();
          ctx.strokeStyle = '#000000';
          ctx.lineWidth = 1;
        }
      }

      time += 0.01 * params.velocity;
      animationRef.current = requestAnimationFrame(draw);
    };

    if (isAnimating) {
      draw();
    }

    return () => {
      window.removeEventListener('resize', resize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [params, isAnimating]);

  return (
    <div className="relative w-full h-full overflow-hidden pointer-events-none">
      {/* Canvas */}
      <canvas
        ref={canvasRef}
        className="w-full h-full cursor-pointer"
        style={{ display: 'block' }}
      />

      {/* Parameter Controls */}
    </div>
  );
}
